import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  FileText, MessageSquare, Plus, Clock, ChevronRight, 
  Package, Users, Eye, Star, ArrowRight, Lock, Crown
} from 'lucide-react';
import { dashboardApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { UpgradeBanner } from '../../components/MembershipGating';

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: dashboardData } = await dashboardApi.getBuyer();
        setData(dashboardData);
      } catch (error) {
        console.error('Error fetching dashboard:', error);
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="container-main py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="grid grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const stats = data?.stats || {};
  const rfqs = data?.rfqs || [];
  const responses = data?.responses || [];
  const conversations = data?.conversations || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      <div className="container-main py-8" data-testid="buyer-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F] mb-1 tracking-tight">Buyer Dashboard</h1>
            <p className="text-slate-500 text-sm">Welcome back, {user?.company_name || user?.name}</p>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button onClick={() => navigate('/rfqs/create')} className="bg-[#EA580C] hover:bg-[#C2410C] rounded-sm font-semibold" data-testid="dashboard-post-rfq">
              <Plus className="w-4 h-4 mr-2" />
              Post Buy Requirement
            </Button>
          </div>
        </div>

        {/* Upgrade Banner */}
        <UpgradeBanner membership={user?.membership} />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-[#2563EB]" />
              <span className="text-sm text-slate-500">Active RFQs</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats.total_rfqs || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#2563EB]" />
              <span className="text-sm text-slate-500">Supplier Responses</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats.total_responses || 0}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <MessageSquare className="w-5 h-5 text-[#2563EB]" />
              <span className="text-sm text-slate-500">Active Conversations</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{stats.active_conversations || 0}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* RFQs and Responses */}
          <div className="lg:col-span-2 space-y-8">
            {/* My RFQs */}
            <div className="bg-white border border-slate-200 rounded-xl">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="font-semibold text-slate-900">My Buy Requirements</h2>
                <Link to="/rfqs" className="text-sm text-[#2563EB] hover:underline">
                  View all
                </Link>
              </div>
              {rfqs.length > 0 ? (
                <div className="divide-y">
                  {rfqs.map((rfq) => (
                    <Link
                      key={rfq.rfq_id}
                      to={`/rfqs/${rfq.rfq_id}`}
                      className="p-4 block hover:bg-slate-50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-slate-900">{rfq.title}</h3>
                            {rfq.is_urgent && (
                              <Badge className="bg-red-50 text-red-700 border-red-200 text-xs">Urgent</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-1">{rfq.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Package className="w-3 h-3" />
                              {rfq.quantity} {rfq.quantity_unit}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {rfq.responses_count} responses
                            </span>
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {rfq.views} views
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">No buy requirements posted yet</p>
                  <Button onClick={() => navigate('/rfqs/create')}>
                    Post Your First Requirement
                  </Button>
                </div>
              )}
            </div>

            {/* Supplier Responses */}
            {responses.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl">
                <div className="p-4 border-b">
                  <h2 className="font-semibold text-slate-900">Recent Supplier Responses</h2>
                </div>
                <div className="divide-y">
                  {responses.slice(0, 5).map((response) => (
                    <div key={response.response_id} className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2563EB] to-[#EA580C] flex items-center justify-center text-white font-bold flex-shrink-0">
                          {response.supplier_name?.charAt(0) || 'S'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900">{response.supplier_name}</p>
                            {response.price_quote && (
                              <span className="text-[#EA580C] font-bold">
                                ${response.price_quote.toFixed(2)}/unit
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 line-clamp-2 mt-1">{response.message}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(response.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recent Conversations */}
            <div className="bg-white border border-slate-200 rounded-xl">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="font-semibold text-slate-900">Messages</h2>
                <Link to="/dashboard/messages" className="text-sm text-[#2563EB] hover:underline">
                  View all
                </Link>
              </div>
              {conversations.length > 0 ? (
                <div className="divide-y">
                  {conversations.slice(0, 5).map((conv) => (
                    <Link
                      key={conv.conversation_id}
                      to="/dashboard/messages"
                      className="p-4 block hover:bg-slate-50"
                    >
                      <p className="font-medium text-slate-900 text-sm">
                        {conv.other_participant?.company_name || conv.other_participant?.name || 'Unknown'}
                      </p>
                      <p className="text-xs text-slate-600 line-clamp-1 mt-1">
                        {conv.last_message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(conv.last_message_at).toLocaleDateString()}
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">No conversations yet</p>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/rfqs/create')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Post Buy Requirement
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/products')}>
                  <Package className="w-4 h-4 mr-2" />
                  Browse Products
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => navigate('/suppliers')}>
                  <Users className="w-4 h-4 mr-2" />
                  Find Suppliers
                </Button>
              </div>
            </div>

            {/* Upgrade CTA */}
            {(!user?.membership || user?.membership === 'free') && (
              <div className="bg-[#0A192F] p-6 text-white">
                <Star className="w-7 h-7 text-[#EA580C] mb-3" />
                <h3 className="font-bold text-base mb-1">Unlock Full Access</h3>
                <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                  View supplier contacts, get priority responses, and access premium features.
                </p>
                <Button onClick={() => navigate('/membership')} className="w-full bg-[#EA580C] hover:bg-[#C2410C] rounded-sm font-semibold">
                  View Plans <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerDashboard;
